import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Percent, Save, Loader2, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/60 disabled:opacity-60";

/**
 * Configuración global → Retenciones de IVA a proveedores.
 * Permisos: retenciones.ver_config (ver) / retenciones.editar_config (guardar).
 * Si una empresa es agente de retención se define en Mis empresas.
 */
export default function RetencionesConfigSection({ token, puedeEditar }) {
  const [cfg, setCfg] = useState(null);
  const [tipos, setTipos] = useState({});
  const [saving, setSaving] = useState(false);
  const [anioNuevo, setAnioNuevo] = useState("");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const cargar = () =>
    fetch(`${API}/admin/retenciones/config`, { headers })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => { setTipos(d.tipos_comprobante || {}); setCfg(d); })
      .catch(() => setCfg(null));

  useEffect(() => { cargar(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg) return null;

  const guardar = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/retenciones/config`, {
        method: "PUT", headers,
        body: JSON.stringify({
          sueldos_minimos: cfg.sueldos_minimos, dias_jornal: cfg.dias_jornal,
          jornales_umbral: cfg.jornales_umbral, tabla: cfg.tabla,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo guardar");
      setCfg(data);
      toast.success("Configuración de retenciones guardada. Rige para los pagos nuevos.");
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const anios = Object.keys(cfg.sueldos_minimos || {}).sort();
  const umbralAnio = (a) => {
    const s = Number(cfg.sueldos_minimos[a]);
    return s ? Math.round((s / Number(cfg.dias_jornal || 26)) * Number(cfg.jornales_umbral || 10)) : 0;
  };
  const setFila = (i, k, v) => setCfg(c => ({ ...c, tabla: c.tabla.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));

  return (
    <section className="bg-arandu-dark-light border border-white/10 rounded-2xl p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
          <Percent className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h2 className="text-white font-heading text-base">Retenciones de IVA (compras)</h2>
          <p className="text-slate-400 text-xs mt-0.5">
            Rige para las empresas marcadas como agente de retención en Mis empresas. Solo afecta pagos nuevos.
          </p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <p className="text-slate-300 text-xs font-medium mb-2">Sueldo mínimo por ejercicio (se usa el vigente al inicio del año)</p>
          <div className="space-y-2">
            {anios.map(a => (
              <div key={a} className="grid grid-cols-[80px_1fr_auto_auto] gap-2 items-center">
                <span className="text-white text-sm">{a}</span>
                <input type="number" className={inp} disabled={!puedeEditar} value={cfg.sueldos_minimos[a]}
                  onChange={e => setCfg(c => ({ ...c, sueldos_minimos: { ...c.sueldos_minimos, [a]: e.target.value } }))} />
                <span className="text-slate-400 text-[11px] whitespace-nowrap">Umbral s/IVA: ₲ {umbralAnio(a).toLocaleString("es-PY")}</span>
                {puedeEditar && anios.length > 1 && (
                  <button type="button" className="text-slate-500 hover:text-red-400"
                    onClick={() => setCfg(c => { const n = { ...c.sueldos_minimos }; delete n[a]; return { ...c, sueldos_minimos: n }; })}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {puedeEditar && (
              <div className="flex gap-2">
                <input type="number" placeholder="Año (ej: 2027)" className={`${inp} max-w-[160px]`} value={anioNuevo} onChange={e => setAnioNuevo(e.target.value)} />
                <button type="button" className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs flex items-center gap-1"
                  onClick={() => { if (anioNuevo) { setCfg(c => ({ ...c, sueldos_minimos: { ...c.sueldos_minimos, [anioNuevo]: c.sueldos_minimos[anioNuevo] || "" } })); setAnioNuevo(""); } }}>
                  <Plus className="w-3.5 h-3.5" /> Agregar año
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3 max-w-md">
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Días del jornal (sueldo ÷ días)</label>
              <input type="number" className={inp} disabled={!puedeEditar} value={cfg.dias_jornal} onChange={e => setCfg(c => ({ ...c, dias_jornal: e.target.value }))} />
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1 block">Jornales del umbral</label>
              <input type="number" className={inp} disabled={!puedeEditar} value={cfg.jornales_umbral} onChange={e => setCfg(c => ({ ...c, jornales_umbral: e.target.value }))} />
            </div>
          </div>
        </div>

        <div>
          <p className="text-slate-300 text-xs font-medium mb-2">Porcentaje a retener según tipo de comprobante</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400">
                <tr>
                  <th className="text-left py-1 pr-2">Comprobante</th>
                  <th className="text-left py-1 pr-2">IVA</th>
                  <th className="text-left py-1 pr-2">% del IVA retenido</th>
                  <th className="text-left py-1 pr-2" title="Reservado: la Renta todavía no se calcula">% Renta (reservado)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cfg.tabla.map((r, i) => (
                  <tr key={i}>
                    <td className="pr-2 py-1">
                      <select className={inp} disabled={!puedeEditar} value={r.tipo} onChange={e => setFila(i, "tipo", e.target.value)}>
                        {Object.entries(tipos).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </td>
                    <td className="pr-2 py-1 w-24">
                      <select className={inp} disabled={!puedeEditar} value={r.tasa} onChange={e => setFila(i, "tasa", Number(e.target.value))}>
                        <option value={10}>10%</option><option value={5}>5%</option><option value={0}>Exento</option>
                      </select>
                    </td>
                    <td className="pr-2 py-1 w-32"><input type="number" min="0" max="100" className={inp} disabled={!puedeEditar} value={r.pct_iva} onChange={e => setFila(i, "pct_iva", e.target.value)} /></td>
                    <td className="pr-2 py-1 w-32"><input type="number" min="0" max="100" className={inp} disabled={!puedeEditar} value={r.pct_renta} onChange={e => setFila(i, "pct_renta", e.target.value)} /></td>
                    <td className="py-1">
                      {puedeEditar && (
                        <button type="button" className="text-slate-500 hover:text-red-400" onClick={() => setCfg(c => ({ ...c, tabla: c.tabla.filter((_, j) => j !== i) }))}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {puedeEditar && (
            <button type="button" className="mt-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs flex items-center gap-1"
              onClick={() => setCfg(c => ({ ...c, tabla: [...c.tabla, { tipo: "normal", tasa: 10, pct_iva: 0, pct_renta: 0 }] }))}>
              <Plus className="w-3.5 h-3.5" /> Agregar regla
            </button>
          )}
          <p className="text-slate-500 text-[11px] mt-2">
            Si un comprobante no tiene regla para su tasa de IVA, no se retiene. Los comprobantes exentos quedan en 0.
          </p>
        </div>

        {puedeEditar ? (
          <div className="flex justify-end">
            <button type="button" onClick={guardar} disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar retenciones
            </button>
          </div>
        ) : (
          <p className="text-slate-500 text-xs">Solo lectura (falta el permiso retenciones.editar_config).</p>
        )}
      </div>
    </section>
  );
}

